import { useMemo, useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Nav, Tab } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { isEqual } from "lodash";
import AiccBreadCrumb from "components/common/AiccBreadCrumb";
import PrimaryButton from "components/Form/Buttons/PrimaryButton";
import CenterLoading from "components/common/CenterLoading";
import { ContentCard } from "components/ContentCard";
import SettingsTab from "../components/createRule/SettingsTab";
import RuleCreationTab from "../components/createRule/RuleCreationTab";
import RuleHistoryTab from "../components/createRule/RuleHistoryTab";
import {
  RuleDataProvider,
  useRuleDataContext,
} from "../context/RuleDataProvider";
import rulesApi from "services/apis/rulesApi";
import companyUserApi from "services/apis/companyUserApi";
import {
  CRUD_RULE_ENGINE_TYPES,
  getPathPrefix,
  nameCapitalize,
} from "utils/coreUtils";
import AICCRuleBuilderUtils from "utils/AICCRuleBuilderUtils";
import RuleBuilderConvert from "utils/converter/RuleBuilderConvert";
import { useFormContext } from "react-hook-form";
import { RuleVersionCrudType } from "../types";
import "../../../assets/scss/queryBuilder.scss";

const VersionRuleFormComponent = () => {
  const queryChangeRef = useRef(null);
  const navigate = useNavigate();
  const params = useParams();
  const [t] = useTranslation();
  const { type, query, isFormChanged, isLoading, onSubmitRule } =
    useRuleDataContext();
  const {
    getValues,
    formState: { isValid },
  } = useFormContext();
  const viewOnly = type === RuleVersionCrudType.VIEW;
  const { datastream } = useSelector(
    (state: any) => state?.datastream?.selectedDS
  );

  const [activeTab, setActiveTab] = useState("setting");
  const [ruleOverviewDropdown, setRuleOverviewDropdown] = useState([]);
  const [ruleHistory, setRuleHistory] = useState([]);

  const breadCrumbTitle = useMemo(() => {
    if (type === CRUD_RULE_ENGINE_TYPES.CREATE) {
      return t("versionsForm.createTitle");
    }
    if (type === CRUD_RULE_ENGINE_TYPES.EDIT) {
      return t("versionsForm.updateTitle");
    }
    if (viewOnly) {
      return t("versionsForm.viewTitle");
    }

    return t("versionsForm.createTitle");
  }, [t, type]);

  useEffect(() => {
    getAllGroupByName();
  }, [type]);

  async function getAllGroupByName() {
    try {
      const response = await rulesApi.getGroupNameByGroup({
        dsConfigVersionId: params?.versionId,
      });
      setRuleOverviewDropdown(
        response.data.map((e, index) => {
          const value = nameCapitalize(e);
          return {
            label: value,
            value,
          };
        })
      );
    } catch (error) {
      console.error(error);
    }
  }

  const isRuleQueryValid = useMemo(() => {
    const queryWhereClauses =
      RuleBuilderConvert.aiccRBQueryToWhereClause(query);
    if (
      type === RuleVersionCrudType.EDIT &&
      !queryChangeRef.current &&
      queryWhereClauses.length
    ) {
      queryChangeRef.current = queryWhereClauses;
    }
    const hasQueryChanged = !isEqual(queryChangeRef.current, queryWhereClauses);
    const hasActiveQuery = query?.some((q) => !q.isDeleted);
    const isIncompletedQuery = query
      ?.filter((q) => !q.isDeleted)
      .some((q, index, arr) => {
        const isRuleComplete = AICCRuleBuilderUtils.checkQueryValidation(
          q,
          index === arr.length - 1
        );
        return !isRuleComplete;
      });
    return hasActiveQuery && !isIncompletedQuery && hasQueryChanged;
  }, [query]);

  const fetchUserDetails = async (id) => {
    const response = await companyUserApi.getCompanyUserDetailsById({
      companyUserId: id,
    });
    return response.data;
  };

  useEffect(() => {
    const getRuleVersions = async () => {
      const response = await rulesApi.getRuleHistoryVersions({
        rulesId: params?.overviewId,
      });
      const uniqueUserIds = response.data.reduce((ids, historyData) => {
        if (ids.indexOf(historyData.createdBy) < 0) {
          ids.push(historyData.createdBy);
        }
        return ids;
      }, []);
      const userDetailsPromises = uniqueUserIds.map((id) =>
        fetchUserDetails(id)
      );
      const userDetailsArray = await Promise.all(userDetailsPromises);
      const userDetailsMap = {};
      userDetailsArray?.forEach((user) => {
        userDetailsMap[user.companyUserId] = user;
      });
      setRuleHistory(
        response.data?.map((historyData) => {
          const userName = `${userDetailsMap[historyData.createdBy]?.firstName} ${userDetailsMap[historyData.createdBy]?.lastName}`;
          return {
            ...historyData,
            createdBy: userName.trim(),
          };
        })
      );
    };
    if (params?.overviewId) {
      getRuleVersions();
    }
  }, []);

  const canSave =
    type === RuleVersionCrudType.EDIT
      ? (isFormChanged && isValid) || isRuleQueryValid
      : isValid && isRuleQueryValid;
  return (
    <>
      <div>
        <AiccBreadCrumb
          homeLink={getPathPrefix(`selected-datastream/rule-engine/versions/`)}
          title={datastream?.customDataStreamName}
          items={t("createRuleEngine.breadCrumb.items", {
            returnObjects: true,
          })}
        />
        <ContentCard
          headerImgSrc="/assets/images/engine.svg"
          title={`${t("createRuleEngine.cardTitle")} - ${breadCrumbTitle}`}
          bodyContent={
            <>
              {isLoading ? (
                <CenterLoading />
              ) : (
                <div className="row">
                  <Tab.Container
                    id="v-pills-tab"
                    defaultActiveKey={activeTab}
                    onSelect={(selectedTab) => {
                      setActiveTab(selectedTab);
                    }}
                  >
                    <div className="col-xl-12">
                      <div className="col-xl-12">
                        <Nav
                          variant="pills"
                          className="nav-pills-custom justify-content-md-center justify-content-lg-start"
                        >
                          <Nav.Item>
                            <Nav.Link eventKey="setting">
                              <i className="fa-solid fa-circle-dot me-2"></i>
                              {t("createRuleEngine.topTab.settings")}
                            </Nav.Link>
                          </Nav.Item>
                          <Nav.Item>
                            <Nav.Link eventKey="rule-creation">
                              <i className="fa-solid fa-circle-dot me-2"></i>
                              {t("createRuleEngine.topTab.ruleCreation")}
                            </Nav.Link>
                          </Nav.Item>
                          {params?.overviewId && (
                            <Nav.Item>
                              <Nav.Link eventKey="rule-history">
                                <i className="fa-solid fa-circle-dot me-2"></i>
                                {t("createRuleEngine.topTab.history")}
                              </Nav.Link>
                            </Nav.Item>
                          )}
                        </Nav>
                      </div>
                      <div className="col-xl-12">
                        <Tab.Content id="v-pills-tabContent">
                          <Tab.Pane
                            eventKey="rule-creation"
                            className="rounded-container"
                          >
                            <RuleCreationTab
                              viewOnly={viewOnly}
                              onRuleReset={() => {
                                queryChangeRef.current = null;
                              }}
                            />
                          </Tab.Pane>

                          <Tab.Pane eventKey="setting">
                            <SettingsTab
                              viewOnly={viewOnly}
                              ruleOverviewDropdown={ruleOverviewDropdown}
                            />
                          </Tab.Pane>
                          <Tab.Pane eventKey="rule-history">
                            <RuleHistoryTab ruleHistory={ruleHistory} />
                          </Tab.Pane>
                        </Tab.Content>
                      </div>
                    </div>
                  </Tab.Container>
                  <div className="d-flex gap-2 mt-4 justify-content-end">
                    <PrimaryButton
                      title={t("CommonKeys.backButtonTitle")}
                      variant="secondary"
                      icon="fa-solid fa-arrow-left"
                      onClick={() => {
                        navigate(-1);
                      }}
                    />
                    {!viewOnly && activeTab !== "rule-history" && (
                      <PrimaryButton
                        title={t("CommonKeys.saveButtonTitle")}
                        icon="fa-regular fa-floppy-disk"
                        onClick={() => {
                          onSubmitRule(false);
                        }}
                        disabled={!canSave}
                      />
                    )}
                  </div>
                </div>
              )}
            </>
          }
        ></ContentCard>
      </div>
    </>
  );
};

const VersionRuleForm = () => {
  return (
    <RuleDataProvider>
      <VersionRuleFormComponent />
    </RuleDataProvider>
  );
};

export default VersionRuleForm;
